import React, { useState, useCallback } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import Modal from '../../../../components/Modal';
import './styles.scss';

interface AddClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClientCreated: () => void;
}

interface FormErrors {
  company_name?: string;
  general?: string;
}

const AddClientModal: React.FC<AddClientModalProps> = ({
  isOpen,
  onClose,
  onClientCreated
}) => {
  const supabase = useSupabaseClient();

  const [companyName, setCompanyName] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (!companyName.trim()) {
      setErrors({ company_name: 'Company name is required' });
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const { error } = await supabase
        .from('clients')
        .insert({ company_name: companyName.trim() });

      if (error) throw error;

      onClientCreated();
      handleClose();
    } catch (err) {
      console.error('Error creating client:', err);
      setErrors({
        general: err instanceof Error ? err.message : 'Failed to create client'
      });
    } finally {
      setIsSubmitting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyName, supabase, onClientCreated]);

  const handleClose = useCallback(() => {
    setCompanyName('');
    setErrors({});
    onClose();
  }, [onClose]);

  const footerContent = (
    <div className="modal-footer-actions">
      <button
        type="button"
        className="btn-secondary"
        onClick={handleClose}
        disabled={isSubmitting}
      >
        Cancel
      </button>
      <button
        type="button"
        className="btn-primary"
        onClick={handleSubmit}
        disabled={isSubmitting || !companyName.trim()}
      >
        {isSubmitting ? 'Creating...' : 'Create Client'}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Client"
      size="sm"
      hasFooter={true}
      footerContent={footerContent}
    >
      <div className="form-content">
        {errors.general && (
          <div className="error-message general-error">{errors.general}</div>
        )}

        <div className="form-group">
          <label htmlFor="company_name">
            Company Name <span className="required">*</span>
          </label>
          <input
            id="company_name"
            type="text"
            value={companyName}
            onChange={(e) => {
              setCompanyName(e.target.value);
              if (errors.company_name) {
                setErrors(prev => ({ ...prev, company_name: undefined }));
              }
            }}
            placeholder="Enter company name"
            className={errors.company_name ? 'error' : ''}
            disabled={isSubmitting}
            autoFocus
          />
          {errors.company_name && <div className="error-message">{errors.company_name}</div>}
        </div>
      </div>
    </Modal>
  );
};

export default AddClientModal;
